import { useEffect, useId, useRef, useState } from 'react';

import { Badge, Field, Icon } from '@/components/ui';
import { cn } from '@/utils/cn';

import { formatPrice } from './money';

/**
 * Pick any number of plans, shown as removable chips.
 *
 * The list opens inline, under the field, rather than as a floating popover:
 * this lives inside scrolling modals, where an absolutely positioned panel is
 * clipped by the dialog body. The list is a multi-select listbox -- arrow keys
 * move, Space or Enter toggles, Escape closes it and returns to the toggle --
 * so the whole control works without a mouse.
 *
 * `plans` are the choices (`{code, name, price_cents, currency, interval,
 * is_active}`); `value` is an array of plan codes, and a code with no plan in
 * `plans` still shows as a chip so an old restriction can be seen and removed.
 * An empty `value` means "no restriction", described by `emptyLabel`.
 */
export function PlanMultiSelect({
  label,
  plans,
  value,
  onChange,
  hint,
  error,
  disabled = false,
  emptyLabel = 'Any paid plan',
  noPlansLabel = 'There are no paid plans to choose from.',
}) {
  const toggleId = useId();
  const listId = useId();
  const listRef = useRef(null);
  const toggleRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const byCode = new Map(plans.map((plan) => [plan.code, plan]));
  const current = Math.min(active, Math.max(plans.length - 1, 0));

  // Focus follows the list open, so the arrow keys work straight away.
  useEffect(() => {
    if (open) listRef.current?.focus();
  }, [open]);

  function toggle(code) {
    onChange(value.includes(code) ? value.filter((chosen) => chosen !== code) : [...value, code]);
  }

  function close() {
    setOpen(false);
    toggleRef.current?.focus();
  }

  function onListKeyDown(event) {
    const last = plans.length - 1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(current >= last ? 0 : current + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(current <= 0 ? last : current - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActive(last);
    } else if (event.key === ' ' || event.key === 'Enter') {
      // Never submit the form around it.
      event.preventDefault();
      if (plans[current]) toggle(plans[current].code);
    } else if (event.key === 'Escape') {
      // The surrounding modal closes on Escape too; this one only closes the list.
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  }

  function onToggleKeyDown(event) {
    if (event.key === 'ArrowDown' && !open) {
      event.preventDefault();
      setOpen(true);
    }
  }

  return (
    <Field label={label} htmlFor={toggleId} hint={hint} error={error}>
      <div
        className={cn(
          'border-line bg-surface rounded-input flex min-h-9 w-full flex-wrap items-center gap-1.5 border px-2 py-1 shadow-xs transition',
          !disabled &&
            'hover:border-brand-300 focus-within:border-brand-500 focus-within:ring-brand-100 focus-within:ring-2',
          error && 'border-danger-500 hover:border-danger-500',
          disabled && 'opacity-60',
        )}
      >
        {value.map((code) => {
          const plan = byCode.get(code);
          return (
            <span
              key={code}
              className="bg-brand-50 text-brand-700 ring-brand-100 inline-flex items-center gap-1 rounded-md py-0.5 pr-1 pl-2 text-[13px] font-medium ring-1 ring-inset"
            >
              {plan?.name ?? code}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggle(code)}
                  className="hover:bg-brand-100 grid size-4 place-items-center rounded"
                  aria-label={`Remove ${plan?.name ?? code}`}
                >
                  <Icon name="close" size={12} />
                </button>
              )}
            </span>
          );
        })}
        <button
          ref={toggleRef}
          id={toggleId}
          type="button"
          disabled={disabled}
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((previous) => !previous)}
          onKeyDown={onToggleKeyDown}
          className="text-ink-muted hover:text-ink flex min-w-[8rem] flex-1 items-center justify-between gap-2 py-0.5 text-left text-sm outline-none disabled:cursor-not-allowed"
        >
          <span>
            {value.length === 0 ? emptyLabel : open ? 'Done choosing' : 'Add or remove plans'}
          </span>
          <Icon
            name="chevron"
            size={14}
            className={cn('shrink-0 transition', open ? '-rotate-90' : 'rotate-90')}
          />
        </button>
      </div>

      {open && (
        <div className="border-line bg-surface rounded-lg border p-1 shadow-xs">
          {plans.length === 0 ? (
            <p className="text-ink-muted px-2.5 py-2 text-sm">{noPlansLabel}</p>
          ) : (
            <ul
              ref={listRef}
              id={listId}
              role="listbox"
              aria-multiselectable="true"
              aria-label={label}
              aria-activedescendant={`${listId}-${current}`}
              tabIndex={0}
              onKeyDown={onListKeyDown}
              className="max-h-56 overflow-y-auto rounded-md outline-none focus-visible:ring-brand-100 focus-visible:ring-2"
            >
              {plans.map((plan, index) => {
                const checked = value.includes(plan.code);
                return (
                  <li
                    key={plan.code}
                    id={`${listId}-${index}`}
                    role="option"
                    aria-selected={checked}
                    onClick={() => {
                      setActive(index);
                      toggle(plan.code);
                    }}
                    onMouseEnter={() => setActive(index)}
                    className={cn(
                      'flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm',
                      index === current ? 'bg-brand-50' : '',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'grid size-4 shrink-0 place-items-center rounded border',
                        checked ? 'border-brand-600 bg-brand-600 text-white' : 'border-line',
                      )}
                    >
                      {checked && <Icon name="check" size={12} />}
                    </span>
                    <span className="text-ink min-w-0 flex-1 truncate">{plan.name}</span>
                    {plan.is_active === false && <Badge tone="neutral">Retired</Badge>}
                    <span className="text-ink-muted shrink-0 text-xs tabular-nums">
                      {formatPrice(plan.price_cents, plan.currency, plan.interval)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="border-line mt-1 flex items-center justify-between gap-2 border-t px-1.5 pt-1">
            <button
              type="button"
              disabled={value.length === 0}
              onClick={() => onChange([])}
              className="text-ink-muted hover:text-ink rounded px-1 py-1 text-xs disabled:opacity-50"
            >
              Clear — {emptyLabel.toLowerCase()}
            </button>
            <button
              type="button"
              onClick={close}
              className="text-brand-700 hover:bg-brand-50 rounded px-2 py-1 text-xs font-medium"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </Field>
  );
}
